import {announce} from '@github-ui/aria-live'
import {useReopenPullRequestMutation} from '@github-ui/mergebox/use-reopen-pull-request-mutation'
import {GitPullRequestClosedIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {useInvalidatePrState} from '../../../../hooks/use-invalidate-pr-state'
import {FailureDialog} from '../../../FailureDialog'
import {ForYouItem} from '../shared/ForYouItem'
import {PullRequestState} from '../../../../types'
import {useRef} from 'react'
import {findPreviousTabbableElementOutsideRow} from './focus-utils'

export function ReopenPullRequestAction() {
  const invalidatePrState = useInvalidatePrState()
  const reopenButtonRef = useRef<HTMLButtonElement>(null)
  const reopenMutation = useReopenPullRequestMutation({
    onSuccess: () => {
      findPreviousTabbableElementOutsideRow(reopenButtonRef.current)?.focus()
      announce('Pull request reopened')
      invalidatePrState(PullRequestState.Open)
    },
  })

  return (
    <>
      <Button
        ref={reopenButtonRef}
        size="small"
        loading={reopenMutation.isPending}
        loadingAnnouncement="Reopening pull request"
        onClick={() => reopenMutation.mutate()}
        labelWrap
      >
        Reopen pull request
      </Button>
      {reopenMutation.isError && (
        <FailureDialog
          title="Failed to reopen pull request"
          message="An error occurred while reopening this pull request. Please try again."
          details={reopenMutation.error.message}
          onRetry={() => reopenMutation.mutate()}
          onDismiss={() => reopenMutation.reset()}
        />
      )}
    </>
  )
}

export function ReopenPullRequest({canReopen}: {canReopen: boolean}) {
  return (
    <ForYouItem
      itemId="reopen-pull-request"
      visual={{kind: 'icon', icon: GitPullRequestClosedIcon, tone: 'neutral', glyphSize: 12}}
      title="Closed"
      subText={canReopen ? 'Reopen it if you still need it.' : 'Nothing to do.'}
      actions={
        canReopen
          ? [
              {
                id: 'reopen',
                content: <ReopenPullRequestAction />,
              },
            ]
          : undefined
      }
    />
  )
}
